import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import { OpportunityCard, OPPORTUNITY_DND_TYPE } from './OpportunityCard'

// Start fetching a little before the last card actually clears the bottom edge,
// so a steady scroll doesn't stall on an empty gap waiting for the response.
const LOAD_MORE_MARGIN = '160px'

// The drop preview opens a gap by translating cards, which moves what they
// look like but not their layout. Everything below reads layout
// (offsetTop/offsetHeight, relative to the positioned .cards), so the gap can
// never shift the slot boundaries it is drawn from.
const cardsIn = (container) => [...container.querySelectorAll('[data-opportunity-id]')]
const withoutDragged = (cards, draggedId) => cards.filter((el) => el.dataset.opportunityId !== String(draggedId))
const gapOf = (container) => parseFloat(getComputedStyle(container).rowGap) || 0

// The slot a drop at clientY `y` lands in, counting every card but the dragged
// one: before the first card whose middle is below the pointer.
export const insertionIndex = (container, draggedId, y) => {
    if (!container) return 0
    const top = y - container.getBoundingClientRect().top - container.clientTop + container.scrollTop
    const cards = withoutDragged(cardsIn(container), draggedId)
    const index = cards.findIndex((el) => top < el.offsetTop + el.offsetHeight / 2)
    return index === -1 ? cards.length : index
}

// Where the placeholder for slot `index` starts: the resting top of the card
// it goes before, or one gap below the last card.
export const slotTop = (container, draggedId, index) => {
    const all = cardsIn(container)
    const others = withoutDragged(all, draggedId)
    if (index < others.length) return others[index].offsetTop
    const last = all[all.length - 1]
    if (last) return last.offsetTop + last.offsetHeight + gapOf(container)
    return parseFloat(getComputedStyle(container).paddingTop) || 0
}

export const PipelineColumn = ({
    stage,
    column,
    count,
    allStages,
    onDropOpportunity,
    onOpenOpportunity,
    onLoadMore,
    selection,
}) => {
    const { rows = [], page = 0, hasMore = false, loading = false, loadingMore = false, error = false } = column || {}
    // No page has landed yet. A page-1 refresh keeps showing what it has.
    const firstLoad = loading && page === 0

    // Where a drop would land while a card hovers this column: its slot, the
    // placeholder's top, and how far the cards below it move to open the gap.
    const [preview, setPreview] = useState(null)
    // True for the one render that swaps the preview for the real reorder, so
    // the cards don't animate out of a gap they are now laid out in.
    const [settling, setSettling] = useState(false)
    const cardsRef = useRef(null)
    const slotAt = (item, monitor) => {
        const offset = monitor.getClientOffset()
        return offset ? insertionIndex(cardsRef.current, item.id, offset.y) : 0
    }
    const previewAt = (item, monitor) => {
        const container = cardsRef.current
        if (!container) return
        const index = slotAt(item, monitor)
        const top = slotTop(container, item.id, index)
        const shift = (item.height || 0) + gapOf(container)
        // hover fires on every dragover; re-render only when the slot moves.
        setPreview((prev) => (prev && prev.index === index && prev.top === top && prev.shift === shift
            ? prev : { index, top, height: item.height || 0, shift }))
    }

    const [{ isOver, canDrop, dragged }, dropRef] = useDrop(
        () => ({
            accept: OPPORTUNITY_DND_TYPE,
            // No handler means this board can't move anything (the attach
            // picker) — refuse the drop rather than reaching for a callback
            // that isn't there.
            canDrop: () => Boolean(onDropOpportunity),
            hover: (item, monitor) => {
                if (onDropOpportunity) previewAt(item, monitor)
            },
            drop: (item, monitor) => {
                if (!onDropOpportunity) return
                setSettling(true)
                onDropOpportunity(item.id, stage.id, slotAt(item, monitor))
            },
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                canDrop: monitor.canDrop(),
                dragged: monitor.getItem(),
            }),
        }),
        [stage.id, onDropOpportunity]
    )

    useEffect(function forgetPreview() {
        if (!isOver) setPreview(null)
    }, [isOver])

    useEffect(function settle() {
        if (!settling) return undefined
        // Two frames: the reorder has been painted without transitions.
        let second
        const first = requestAnimationFrame(() => { second = requestAnimationFrame(() => setSettling(false)) })
        return () => { cancelAnimationFrame(first); cancelAnimationFrame(second) }
    }, [settling])

    // The gap: every card from the one the slot sits before (the dragged card
    // too, if it is below it) moves down by the dragged card's height. None
    // where the drop would leave the card where it already is.
    const showGap = isOver && canDrop && preview !== null
        && !(dragged?.fromStage === stage.id && rows.findIndex((o) => o.id === dragged.id) === preview.index)
    const others = showGap ? rows.filter((o) => o.id !== dragged.id) : []
    const shiftFrom = showGap && preview.index < others.length ? rows.indexOf(others[preview.index]) : rows.length

    // This column's own scroller is the observer root — the board itself only
    // scrolls sideways (.board-wrap is overflow-y: hidden), so there is no
    // page-level "bottom" to watch. react-dnd hands back a connector function,
    // so the node is captured here as well to serve as that root.
    const sentinelRef = useRef(null)
    const setCardsRef = useCallback((node) => {
        cardsRef.current = node
        dropRef(node)
    }, [dropRef])

    useEffect(function watchForColumnEnd() {
        // jsdom has no IntersectionObserver; degrade to no lazy loading rather
        // than taking the board down with it.
        if (typeof IntersectionObserver === 'undefined') return undefined
        const root = cardsRef.current
        const sentinel = sentinelRef.current
        // Nothing left to ask for — don't even observe, so a column that has
        // reached its end can't fire another request however far it is scrolled.
        if (!root || !sentinel || !hasMore) return undefined

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return
                // The store re-checks hasMore and both loading flags, so a burst
                // of intersections while a page is already in flight collapses
                // to the one request.
                onLoadMore(stage.id)
            },
            { root, rootMargin: LOAD_MORE_MARGIN }
        )
        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [hasMore, onLoadMore, stage.id])

    return (
        <div className="col">
            <div className="col-header">
                <div className="col-name" title={stage.name}>{stage.name}</div>
                {/* The stage's real size, from the aggregate endpoint — not
                    rows.length, which is only however far this column has been
                    scrolled. */}
                <div className="col-count">{count}</div>
            </div>
            <div
                ref={setCardsRef}
                className={`cards${settling ? ' is-settling' : ''}`}
            >
                {rows.map((opp, index) => (
                    <OpportunityCard
                        key={opp.id}
                        opportunity={opp}
                        onOpen={onOpenOpportunity}
                        stages={allStages}
                        onMove={onDropOpportunity}
                        selection={selection}
                        dropShift={showGap && index >= shiftFrom ? preview.shift : 0}
                    />
                ))}

                {/* The dragged card's size, where it will land. */}
                {showGap && (
                    <div
                        className="drop-placeholder"
                        style={{ top: preview.top, height: preview.height }}
                        aria-hidden="true"
                    />
                )}

                {/* Only after the first request has answered: an empty column and
                    a column that hasn't loaded yet are different things. */}
                {firstLoad && <div className="col-status">Loading…</div>}
                {!firstLoad && rows.length === 0 && (
                    <div className="col-status col-empty">No opportunities</div>
                )}

                {/* A failed next page keeps the cards it already has and offers
                    the retry, rather than emptying the column. */}
                {error && !loadingMore && (
                    <button type="button" className="col-retry" onClick={() => onLoadMore(stage.id)}>
                        Couldn’t load more. Retry
                    </button>
                )}
                {loadingMore && <div className="col-status">Loading more…</div>}

                {/* The lazy trigger. Rendered only while there is a next page, so
                    reaching the end of a column is inert. */}
                {hasMore && !error && <div ref={sentinelRef} className="col-sentinel" aria-hidden="true" />}
            </div>
        </div>
    )
}
